<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>

    <admin-breadcrumb
        :items="[{ text: 'Reports', path: { name: 'admin-reports' } }]"
        name="Monthly Account Revenue"
        :title="pageTitle"
    />

    <admin-panel with-header>
      <template #header>
        <div class="monthly-revenue-header">
          <div class="monthly-revenue-filters">
            <div class="monthly-revenue-filter">
              <label for="revenue-state-filter">State</label>

              <b-form-select
                  id="revenue-state-filter"
                  v-model="filters.state"
                  :options="stateOptions"
                  @change="onFiltersChanged"
              />
            </div>

            <div class="monthly-revenue-filter">
              <label for="subscription-plan-filter">
                Subscription Plan
              </label>

              <b-form-select
                  id="subscription-plan-filter"
                  v-model="filters.subscription_plan_id"
                  :options="subscriptionPlanOptions"
                  @change="onFiltersChanged"
              />
            </div>

            <div class="monthly-revenue-filter">
              <label for="addon-filter">Add-On</label>

              <b-form-select
                  id="addon-filter"
                  v-model="filters.addon_id"
                  :options="addonOptions"
                  @change="onFiltersChanged"
              />
            </div>

            <div
                class="monthly-revenue-filter monthly-revenue-filter--search"
            >
              <label for="revenue-company-search">
                Company / Account
              </label>

              <b-form-input
                  id="revenue-company-search"
                  v-model.trim="filters.search"
                  placeholder="Search by company name or account ID"
                  @input="onSearchInput"
              />
            </div>
          </div>

          <div class="monthly-revenue-actions">
            <b-btn
                variant="primary"
                class="mr-2"
                v-b-tooltip.hover
                title="Export CSV"
                :disabled="!rows.length"
                @click.prevent="downloadCsv"
            >
              <b-icon-download/>
            </b-btn>

            <b-btn
                variant="primary"
                v-b-tooltip.hover
                title="Reload Report"
                @click.prevent="loadReport"
            >
              <b-icon-arrow-clockwise/>
            </b-btn>
          </div>
        </div>
      </template>

      <div class="revenue-summary mb-4">
        <div class="revenue-summary-card">
          <div class="revenue-summary-card__label">
            Base Monthly Revenue
          </div>

          <div class="revenue-summary-card__value">
            {{ formatCurrency(summary.total_base_monthly_revenue) }}
          </div>
        </div>

        <div class="revenue-summary-card">
          <div class="revenue-summary-card__label">
            Add-On Monthly Revenue
          </div>

          <div class="revenue-summary-card__value">
            {{ formatCurrency(summary.total_addon_monthly_revenue) }}
          </div>
        </div>

        <div class="revenue-summary-card revenue-summary-card--total">
          <div class="revenue-summary-card__label">
            Total Monthly Recurring Revenue
          </div>

          <div class="revenue-summary-card__value">
            {{ formatCurrency(summary.total_monthly_recurring_revenue) }}
          </div>
        </div>
      </div>

      <b-table
          :fields="columns"
          :items="rows"
          table-class="text-nowrap"
          responsive
          hover
          show-empty
          empty-text="No paid accounts found"
      >
        <template #cell(company_name)="data">
          <div class="company-name-cell">
            {{ data.item.company_name }}
          </div>
        </template>

        <template
            v-for="column in sortableColumns"
            #[`head(${column.key})`]
        >
          <button
              :key="`sort-${column.key}`"
              type="button"
              class="report-sort-head btn btn-link p-0 d-inline-flex align-items-center"
              @click="toggleSort(column.key)"
          >
            <span>{{ column.label }}</span>
            <span class="ml-2">{{ sortIcon(column.key) }}</span>
          </button>
        </template>

        <template #cell(base_monthly_amount)="data">
          <span class="base-revenue-value">
            {{ formatCurrency(data.item.base_monthly_amount) }}
          </span>
        </template>

        <template #cell(addons)="data">
          <div
              v-if="data.item.addons.length"
              class="addon-list"
          >
            <div
                v-for="addon in data.item.addons"
                :key="`${data.item.account_id}-${addon.id}`"
                class="addon-list__item"
            >
              <span class="addon-list__name">
                {{ addon.name }}
              </span>

              <span class="addon-list__amount">
                {{ formatCurrency(addon.monthly_amount) }}
              </span>
            </div>
          </div>

          <span v-else>-</span>
        </template>

        <template #cell(total_addon_amount)="data">
          <span class="addon-revenue-value">
            {{ formatCurrency(data.item.total_addon_amount) }}
          </span>
        </template>

        <template #cell(total_monthly_amount)="data">
          <strong class="total-revenue-value">
            {{ formatCurrency(data.item.total_monthly_amount) }}
          </strong>
        </template>
      </b-table>
    </admin-panel>
  </b-container>
</template>

<script>
import AdminPreloader from "../components/AdminPreloader";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import AdminPanel from "../components/AdminPanel";
import {API_ROUTES} from "../api";

const SORTABLE_COLUMN_KEYS = [
  "company_name",
  "base_monthly_amount",
  "total_addon_amount",
  "total_monthly_amount",
];

export default {
  name: "AdminReportAccountMonthlyRevenue",

  components: {
    AdminPanel,
    AdminBreadcrumb,
    AdminPreloader,
  },

  data() {
    return {
      loading: false,
      searchTimer: null,
      reportRows: [],
      availableStates: [],
      availableSubscriptionPlans: [],
      availableAddons: [],
      summary: {
        total_accounts: 0,
        total_base_monthly_revenue: 0,
        total_addon_monthly_revenue: 0,
        total_monthly_recurring_revenue: 0,
      },
      filters: {
        state: null,
        subscription_plan_id: null,
        addon_id: null,
        search: "",
        sort: "company_name",
        sort_direction: "asc",
      },
      columns: [
        {
          key: "company_name",
          label: "Company Name",
          thClass: "border-top-0 company-name-column",
          tdClass: "align-middle company-name-column",
        },
        {
          key: "account_id",
          label: "Account ID",
          thClass: "border-top-0",
          tdClass: "align-middle",
        },
        {
          key: "state",
          label: "State",
          thClass: "border-top-0",
          tdClass: "align-middle",
        },
        {
          key: "subscription_plan",
          label: "Subscription Plan",
          thClass: "border-top-0",
          tdClass: "align-middle",
        },
        {
          key: "base_monthly_amount",
          label: "Base Monthly Amount",
          thClass: "border-top-0",
          tdClass: "align-middle",
        },
        {
          key: "addons",
          label: "Add-Ons",
          thClass: "border-top-0 addon-column",
          tdClass: "align-middle addon-column",
        },
        {
          key: "total_addon_amount",
          label: "Total Add-On Amount",
          thClass: "border-top-0",
          tdClass: "align-middle",
        },
        {
          key: "total_monthly_amount",
          label: "Total Monthly Amount",
          thClass: "border-top-0",
          tdClass: "align-middle",
        },
      ],
    };
  },

  computed: {
    pageTitle() {
      return "Monthly Account Revenue and Add-On Report";
    },

    rows() {
      return this.reportRows.map((item) => ({
        company_name: item?.company_name || "Unknown",
        account_id: item?.account_id ?? "-",
        state: item?.state || "-",
        subscription_plan: item?.subscription_plan || "-",
        subscription_plan_id: item?.subscription_plan_id ?? null,
        base_monthly_amount: Number(
            item?.base_monthly_amount ?? 0
        ),
        addons: Array.isArray(item?.addons)
            ? item.addons
            : [],
        total_addon_amount: Number(
            item?.total_addon_amount ?? 0
        ),
        total_monthly_amount: Number(
            item?.total_monthly_amount ?? 0
        ),
      }));
    },

    sortableColumns() {
      return this.columns.filter((column) =>
          SORTABLE_COLUMN_KEYS.includes(column.key)
      );
    },

    stateOptions() {
      return [
        {
          value: null,
          text: "All States",
        },
        ...this.availableStates.map((state) => ({
          value: state,
          text: state,
        })),
      ];
    },

    subscriptionPlanOptions() {
      return [
        {
          value: null,
          text: "All Subscription Plans",
        },
        ...this.availableSubscriptionPlans.map((plan) => ({
          value: plan.id,
          text: plan.name,
        })),
      ];
    },

    addonOptions() {
      return [
        {
          value: null,
          text: "All Add-Ons",
        },
        ...this.availableAddons.map((addon) => ({
          value: addon.id,
          text: addon.name,
        })),
      ];
    },
  },

  methods: {
    getRequestParams() {
      const params = {
        sort: this.filters.sort,
        sort_direction: this.filters.sort_direction,
      };

      if (this.filters.state) {
        params.state = this.filters.state;
      }

      if (this.filters.subscription_plan_id) {
        params.subscription_plan_id =
            this.filters.subscription_plan_id;
      }

      if (this.filters.addon_id) {
        params.addon_id = this.filters.addon_id;
      }

      if (this.filters.search) {
        params.search = this.filters.search;
      }

      return params;
    },

    loadReport() {
      this.loading = true;

      this.$api.get(
          API_ROUTES.ADMIN_REPORT_ACCOUNT_MONTHLY_REVENUE,
          {
            params: this.getRequestParams(),
          }
      )
          .then(({data}) => {
            this.reportRows = Array.isArray(data?.report_data)
                ? data.report_data
                : [];

            this.availableStates = Array.isArray(
                data?.filters?.states
            )
                ? data.filters.states
                : [];

            this.availableSubscriptionPlans = Array.isArray(
                data?.filters?.subscription_plans
            )
                ? data.filters.subscription_plans
                : [];

            this.availableAddons = Array.isArray(
                data?.filters?.addons
            )
                ? data.filters.addons
                : [];

            this.summary = {
              total_accounts: Number(
                  data?.summary?.total_accounts ?? 0
              ),
              total_base_monthly_revenue: Number(
                  data?.summary?.total_base_monthly_revenue ?? 0
              ),
              total_addon_monthly_revenue: Number(
                  data?.summary?.total_addon_monthly_revenue ?? 0
              ),
              total_monthly_recurring_revenue: Number(
                  data?.summary?.total_monthly_recurring_revenue ?? 0
              ),
            };
          })
          .catch(({response}) => {
            this.reportRows = [];
            this.resetSummary();

            const message = response?.status === 422
                ? "Some report filters are invalid."
                : "Failed to load monthly account revenue report.";

            this.$bvToast.toast(message, {
              title: "Error!",
              variant: "danger",
            });
          })
          .finally(() => {
            this.loading = false;
          });
    },

    resetSummary() {
      this.summary = {
        total_accounts: 0,
        total_base_monthly_revenue: 0,
        total_addon_monthly_revenue: 0,
        total_monthly_recurring_revenue: 0,
      };
    },

    onFiltersChanged() {
      this.loadReport();
    },

    onSearchInput() {
      window.clearTimeout(this.searchTimer);

      this.searchTimer = window.setTimeout(() => {
        this.loadReport();
      }, 400);
    },

    toggleSort(column) {
      if (!SORTABLE_COLUMN_KEYS.includes(column)) {
        return;
      }

      if (this.filters.sort === column) {
        this.filters.sort_direction =
            this.filters.sort_direction === "asc"
                ? "desc"
                : "asc";
      } else {
        this.filters.sort = column;
        this.filters.sort_direction = "asc";
      }

      this.loadReport();
    },

    sortIcon(column) {
      if (this.filters.sort !== column) {
        return "↕";
      }

      return this.filters.sort_direction === "asc"
          ? "↑"
          : "↓";
    },

    formatCurrency(value) {
      const amount = Number(value ?? 0);

      if (Number.isNaN(amount)) {
        return "$0.00";
      }

      return `$${amount.toFixed(2)}`;
    },

    formatAddonsForCsv(addons) {
      if (!Array.isArray(addons) || !addons.length) {
        return "";
      }

      return addons
          .map((addon) =>
              `${addon.name}: ${this.formatCurrency(
                  addon.monthly_amount
              )}`
          )
          .join("; ");
    },

    escapeCsvValue(value) {
      return `"${String(value ?? "").replace(/"/g, '""')}"`;
    },

    getCsvValue(row, key) {
      if (key === "addons") {
        return this.formatAddonsForCsv(row.addons);
      }

      if (
          [
            "base_monthly_amount",
            "total_addon_amount",
            "total_monthly_amount",
          ].includes(key)
      ) {
        return Number(row[key] ?? 0).toFixed(2);
      }

      return row[key] ?? "";
    },

    downloadCsv() {
      const headers = this.columns.map((column) =>
          this.escapeCsvValue(column.label)
      );

      const dataRows = this.rows.map((row) =>
          this.columns.map((column) =>
              this.escapeCsvValue(
                  this.getCsvValue(row, column.key)
              )
          )
      );

      const csv = [
        headers.join(","),
        ...dataRows.map((row) => row.join(",")),
      ].join("\n");

      const blob = new Blob(
          ["\uFEFF", csv],
          {
            type: "text/csv;charset=utf-8;",
          }
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = "account_monthly_revenue.csv";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    },
  },

  created() {
    this.loadReport();
  },

  beforeDestroy() {
    window.clearTimeout(this.searchTimer);
  },
};
</script>

<style scoped>
.monthly-revenue-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 16px;
}

.monthly-revenue-filters {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  flex: 1;
}

.monthly-revenue-filter {
  width: 210px;
}

.monthly-revenue-filter--search {
  width: min(360px, 100%);
}

.monthly-revenue-filter label {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.monthly-revenue-actions {
  display: flex;
  align-items: center;
}

.revenue-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(220px, 1fr));
  gap: 12px;
}

.revenue-summary-card {
  padding: 16px;
  border: 1px solid #dfe4e8;
  border-radius: 8px;
  background: #f8f9fa;
}

.revenue-summary-card--total {
  background: #eaf7ee;
  border-color: #badbcc;
}

.revenue-summary-card__label {
  margin-bottom: 6px;
  color: #6c757d;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.revenue-summary-card__value {
  font-size: 24px;
  font-weight: 700;
}

.report-sort-head {
  cursor: pointer;
  user-select: none;
  color: inherit;
  text-decoration: none;
}

.report-sort-head:hover,
.report-sort-head:focus {
  color: inherit;
  text-decoration: none;
  outline: none;
}

.company-name-cell {
  width: 280px;
  max-width: 280px;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.35;
}

.addon-list {
  display: flex;
  flex-direction: column;
  min-width: 260px;
  gap: 4px;
  white-space: normal;
}

.addon-list__item {
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.addon-list__name {
  overflow-wrap: anywhere;
}

.addon-list__amount {
  flex-shrink: 0;
  font-weight: 600;
}

.base-revenue-value {
  color: #084298;
  font-weight: 600;
}

.addon-revenue-value {
  color: #664d03;
  font-weight: 600;
}

.total-revenue-value {
  color: #0f5132;
}

::v-deep .company-name-column {
  width: 280px;
  min-width: 280px;
  max-width: 280px;
  white-space: normal;
}

::v-deep .addon-column {
  min-width: 280px;
  white-space: normal;
}

@media (max-width: 1100px) {
  .revenue-summary {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .monthly-revenue-filter,
  .monthly-revenue-filter--search {
    width: 100%;
  }

  .monthly-revenue-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>